import React, { FC, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { useModalContext, useStripe } from '@sketch/components'

import {
  DynamicLoadingPage,
  IndexLayoutErrorOld,
  routes,
  useQueryParams,
  useFlag,
} from '@sketch/modules-common'

// Local Components
import { WorkspaceSubscribePaymentDetails } from '../../pages'
import PartnerSubscriptionDoneModal from '../../modals/PartnerSubscriptionDoneModal'
import PartnerExpiredWorkspaceError from '../../components/PartnerExpiredWorkspaceError'

// Utils
import useWorkspaceSubscribeState from '../WorkspaceSubscribeView/WorkspaceSubscribeView.hooks'
import { isWorkspaceSubscriptionActive } from '../../utils'

// Styles
import { WorkspaceSubscribeLayout } from '../../views/WorkspaceSubscribeView/WorkspaceSubscribeView.styles'

// GQL
import { usePartnerSubscribeInformation } from '../../operations'

import {
  useAcceptPartnershipInviteMutation,
  useGetPendingWorkspaceMembershipQuery,
  PublicWorkspaceMinimalFragment,
  useGetAvailablePlansQuery,
  BillingPlanFragment,
} from '@sketch/gql-types'

import config from '@sketch/env-config'

interface WorkspaceBillingViewProps {
  workspace: PublicWorkspaceMinimalFragment
  token: string
}

const WorkspaceBillingView: FC<WorkspaceBillingViewProps> = ({
  workspace,
  token,
}) => {
  const pricesUpdated = useFlag('prices-updated')
  const history = useHistory()
  const { showModal, hideModal } = useModalContext()

  // We already made sure the `workspace.customer` exists in "WorkspaceBillingViewGuard"
  const customer = workspace.customer!

  // Load stripe in order previously
  const { load: loadStripe, status: stripeStatus } = useStripe()

  useEffect(() => {
    loadStripe()
  }, [loadStripe])

  const [state, triggers] = useWorkspaceSubscribeState()
  const {
    loading: loadingPlans,
    error: errorPlans,
    data: plans,
  } = useGetAvailablePlansQuery()

  const { loading, data, error } = usePartnerSubscribeInformation(
    customer.identifier
  )

  const [acceptPartnershipInvite] = useAcceptPartnershipInviteMutation({
    onCompleted: () => {
      history.push(
        routes.WORKSPACE_SETTINGS_PEOPLE.create({
          workspaceId: workspace.identifier,
        })
      )

      showModal(
        PartnerSubscriptionDoneModal,
        { onHide: hideModal, workspaceName: workspace.name },
        { closeOnRouteChange: false }
      )
    },
    onError: 'show-toast',
  })

  const subscription = data?.customer.subscription
  useEffect(() => {
    const numberOfScheduledSeats = subscription?.scheduledSeatsTotal
    const numberOfCurrentSeats = subscription?.currentSeatsTotal
    const numberOfEditors = numberOfScheduledSeats || numberOfCurrentSeats

    if (!state.editors && numberOfEditors) {
      triggers.setEditors(numberOfEditors)
    }
  }, [subscription, triggers, state.editors])

  if (loading || loadingPlans || stripeStatus === 'pending' || !state.editors) {
    return <DynamicLoadingPage />
  }

  const isSubscriptionActive =
    subscription && isWorkspaceSubscriptionActive(subscription?.status)

  if (!isSubscriptionActive) {
    return <PartnerExpiredWorkspaceError />
  }

  if (error || errorPlans || stripeStatus === 'error') {
    return <IndexLayoutErrorOld variant="orange" />
  }

  const plansId = pricesUpdated ? 'pro_plans_ids' : 'standard_plans_ids'
  let partnerYearlyPlan = subscription?.currentPlan

  if (partnerYearlyPlan?.type === 'MONTHLY') {
    partnerYearlyPlan = plans?.availablePlans?.find(
      item => item.id === config[plansId].yearly
    ) as BillingPlanFragment
  }

  return (
    <WorkspaceSubscribeLayout expand variant="orange">
      <WorkspaceSubscribePaymentDetails
        paymentMethod="invoice"
        variant="partner"
        title="Set Up a Subscription"
        workspace={{
          name: workspace.name,
          avatar: workspace.avatar?.small,
        }}
        billingInfo={{
          address: data?.partnerBillingDetails.address?.line1,
          city: data?.partnerBillingDetails.address?.city,
          postalCode: data?.partnerBillingDetails.address?.postalCode,
          state: data?.partnerBillingDetails.address?.state || '',
          country: data?.partnerBillingDetails.address?.country,
          email: data?.partnerBillingDetails.email,
          name: data?.partnerBillingDetails.name,
          taxId: data?.partnerBillingDetails.taxId || undefined,
          tos: false,
        }}
        billSimulation={{
          plan: partnerYearlyPlan!,
          trialEnd: '',
          numberEditors: state.editors,
          onEditorsChange: triggers.setEditors,
          numberExtraSeats: 0,

          showDiscountInput: false,
          discountCode: state.discountCode,
          onValidDiscountCode: triggers.applyDiscountCode,
          onClearDiscountCode: triggers.clearDiscountCode,
          discountError: state.discountError,
          onUpdateDiscountError: triggers.updateDiscountError,
        }}
        onSubmit={async ({ tos }) => {
          await acceptPartnershipInvite({
            variables: {
              membershipToken: token!,
              seats: state.editors!,
              tosAgreed: tos,
            },
          })
        }}
      />
    </WorkspaceSubscribeLayout>
  )
}

const WorkspaceBillingViewGuard: FC = () => {
  const { token } = useQueryParams<'WORKSPACE_SUBSCRIBE_PARTNER'>()

  const { loading, data } = useGetPendingWorkspaceMembershipQuery({
    variables: { membershipToken: token || '', includeCustomer: true },
    skip: !token,
  })

  if (loading) return <DynamicLoadingPage />

  const workspace = data?.pendingWorkspaceMembership
  const isParter = workspace?.role === 'PARTNER'

  if (!token || !workspace || !workspace.workspace.customer || !isParter)
    return <IndexLayoutErrorOld variant="orange" />

  return <WorkspaceBillingView workspace={workspace.workspace} token={token} />
}

export default WorkspaceBillingViewGuard
